import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Column, ErrorText, Row } from "@/components";
import { AutosaveFormEnumSelect } from "@/components/companies/shared/autosave-form/AutosaveFormEnumSelect";
import { AutosaveFormRichTextInput } from "@/components/companies/shared/autosave-form/AutosaveFormRichTextInput";
import { IntakeForm } from "@/components/companies/shared/IntakeForm";
import { ReadOnlyField, ReadOnlyRichTextField } from "@/components/companies/shared/ReadOnlyField";

import { useUpdateSetCompanyIntake } from "@/hooks/updateSets/useUpdateSetCompanyIntake";
import { businessTrendOptions } from "@/lib/constants";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { BusinessTrend, CompanyIntake } from "@/types";


const businessSchema = z.object({
  growthStatus: z.nativeEnum(BusinessTrend).nullable(),
  headcountTrend: z.nativeEnum(BusinessTrend).nullable(),
  revenueAndHeadcountDetails: z.string().nullable(),
  details: z.string().nullable(),
});

export const Business = ({
  companyIntake,
  isEditing,
}: {
  companyIntake: CompanyIntake;
  isEditing: boolean;
}) => {
  const { mutate: updateCompanyIntake, isError } = useUpdateSetCompanyIntake();

  const business = companyIntake.business;

  const form = useForm<z.infer<typeof businessSchema>>({
    resolver: zodResolver(businessSchema),
    defaultValues: business,
  });

  const handleSave = (data: z.infer<typeof businessSchema>) => {
    mixpanelUtils.trackFormSubmission("Business section", MIXPANEL_SCREENS.COMPANY);
    updateCompanyIntake({ updateCompanyIntakeRequest: { business: data } });
  };

  const saveOnBlur = () => {
    void form.handleSubmit(handleSave)();
  };

  const growthStatusLabel =
    businessTrendOptions.find((o) => o.value === business.growthStatus)
      ?.label ?? null;
  const headcountTrendLabel =
    businessTrendOptions.find((o) => o.value === business.headcountTrend)
      ?.label ?? null;

  return (
    <Column className="gap-6">
      {isEditing ? (
        <IntakeForm form={form} onSave={saveOnBlur}>
          <Column className="gap-6">
            <Row className="gap-4">
              <AutosaveFormEnumSelect
                className="flex-1"
                fieldName="growthStatus"
                form={form}
                label="Growth Status"
                onSelect={saveOnBlur}
                options={businessTrendOptions}
              />

              <AutosaveFormEnumSelect
                className="flex-1"
                fieldName="headcountTrend"
                form={form}
                label="Headcount Trend"
                onSelect={saveOnBlur}
                options={businessTrendOptions}
              />
            </Row>

            <AutosaveFormRichTextInput
              editorHeight="150px"
              fieldName="revenueAndHeadcountDetails"
              form={form}
              infoTooltip="What are the reasons for the trends in revenue and headcount?"
              label="Revenue & Headcount Details"
              onBlur={saveOnBlur}
            />

            <AutosaveFormRichTextInput
              editorHeight="150px"
              fieldName="details"
              form={form}
              infoTooltip="What other insights about the business were shared during your tours and meetings with the client?"
              label="Miscellaneous Business Details"
              onBlur={saveOnBlur}
            />

            {isError && (
              <ErrorText errorMessage="An error occurred while saving. Please try again." />
            )}
          </Column>
        </IntakeForm>
      ) : (
        <Column className="gap-4">
          <Row className="gap-4">
            <ReadOnlyField
              className="flex-1"
              label="Growth Status"
              value={growthStatusLabel}
            />
            <ReadOnlyField
              className="flex-1"
              label="Headcount Trend"
              value={headcountTrendLabel}
            />
          </Row>
          <ReadOnlyRichTextField
            label="Revenue & Headcount Details"
            value={business.revenueAndHeadcountDetails}
          />
          <ReadOnlyRichTextField
            label="Miscellaneous Business Details"
            value={business.details}
          />
        </Column>
      )}
    </Column>
  );
};
